import React, { useEffect, useState } from 'react';
import styles from '../UI/DateRangeSelector.module.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CalendarDays, Sparkles, X } from 'lucide-react';

const parseIsoDate = (value) => {
    if (!value) {
        return null;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const PRESET_RANGES = [
    'Today',
    'Yesterday',
    'This Week',
    'Last Week',
    'This Month',
    'Last Month',
    'This Year',
    'Last Year',
    'LifeTime'
];

const normalizeDateOnly = (dateValue) => {
    if (!dateValue) {
        return null;
    }

    return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate());
};

const getTodayDate = () => normalizeDateOnly(new Date());

const clampToToday = (dateValue) => {
    if (!dateValue) {
        return null;
    }

    const normalizedDate = normalizeDateOnly(dateValue);
    const todayDate = getTodayDate();
    return normalizedDate > todayDate ? todayDate : normalizedDate;
};

const isSameDate = (firstDate, secondDate) => {
    return (
        firstDate?.getFullYear() === secondDate?.getFullYear()
        && firstDate?.getMonth() === secondDate?.getMonth()
        && firstDate?.getDate() === secondDate?.getDate()
    );
};

const getPresetBounds = (rangeType) => {
    const today = getTodayDate();
    let start;
    let end;

    switch (rangeType) {
        case 'Today':
            start = today;
            end = today;
            break;
        case 'Yesterday': {
            start = new Date(today);
            start.setDate(start.getDate() - 1);
            end = new Date(start);
            break;
        }
        case 'This Week':
            start = new Date(today);
            start.setDate(start.getDate() - start.getDay());
            end = today;
            break;
        case 'Last Week':
            start = new Date(today);
            start.setDate(start.getDate() - start.getDay() - 7);
            end = new Date(start);
            end.setDate(end.getDate() + 6);
            break;
        case 'This Month':
            start = new Date(today.getFullYear(), today.getMonth(), 1);
            end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            break;
        case 'Last Month':
            start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            end = new Date(today.getFullYear(), today.getMonth(), 0);
            break;
        case 'This Year':
            start = new Date(today.getFullYear(), 0, 1);
            end = new Date(today.getFullYear(), 11, 31);
            break;
        case 'Last Year':
            start = new Date(today.getFullYear() - 1, 0, 1);
            end = new Date(today.getFullYear() - 1, 11, 31);
            break;
        case 'LifeTime':
            start = new Date(2018, 0, 1);
            end = today;
            break;
        default:
            start = null;
            end = null;
    }

    return {
        start,
        end
    };
};

const getMatchingPreset = (startDate, endDate) => {
    const normalizedStart = normalizeDateOnly(startDate);
    const normalizedEnd = normalizeDateOnly(endDate);

    if (!normalizedStart || !normalizedEnd) {
        return null;
    }

    const matchedRange = PRESET_RANGES.find((rangeType) => {
        const { start, end } = getPresetBounds(rangeType);
        return isSameDate(start, normalizedStart) && isSameDate(end, normalizedEnd);
    });

    return matchedRange || null;
};

const formatDateLabel = (dateValue) => {
    if (!dateValue) {
        return '--';
    }

    return dateValue.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const DateRangeSelector = props => {
    const isOpen = props.isOpen;
    const closeModal = props.closeModal;
    const changeDateHandler = props.changeDateHandler;
    const initialStartDate = props.initialStartDate;
    const initialEndDate = props.initialEndDate;

    const [startDate, setStartDate] = useState(clampToToday(parseIsoDate(initialStartDate)) || getTodayDate());
    const [endDate, setEndDate] = useState(clampToToday(parseIsoDate(initialEndDate)) || getTodayDate());
    const [activePreset, setActivePreset] = useState(null);

    useEffect(() => {
        const onWindowClick = (event) => {
            if (event.target.id === "dateRangeSelector") {
                closeModal();
            }

            if (event.target.id === "filtersPanelWrapperMUG") {
                const elem = document.getElementById("filtersPanelWrapperMUG");
                if (elem) {
                    elem.style.animation = "slideRightToLeft .1s forwards";
                }
            }
        };

        const onKeyDown = (e) => {
            if (e.keyCode === 27) {
                closeModal();
            }
        };

        window.addEventListener('click', onWindowClick);
        document.addEventListener('keydown', onKeyDown);

        return () => {
            window.removeEventListener('click', onWindowClick);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [closeModal]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const nextStart = clampToToday(parseIsoDate(initialStartDate));
        const nextEnd = clampToToday(parseIsoDate(initialEndDate));

        if (nextStart) {
            setStartDate(nextStart);
        }
        if (nextEnd) {
            setEndDate(nextEnd);
        }
    }, [initialEndDate, initialStartDate, isOpen]);

    const handleStartChange = (date) => {
        setActivePreset(null);
        setStartDate(clampToToday(date));
    };

    const handleEndChange = (date) => {
        setActivePreset(null);
        setEndDate(clampToToday(date));
    };

    const highlightDates = (date) => {
        if (startDate && endDate) {
            return date >= startDate && date <= endDate;
        } else {
            return false;
        }
    };

    const setDateRange = (rangeType) => {
        const { start, end } = getPresetBounds(rangeType);
        if (!start || !end) {
            return;
        }

        setStartDate(start);
        setEndDate(end);
        setActivePreset(rangeType);
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setActivePreset(getMatchingPreset(startDate, endDate));
    }, [endDate, isOpen, startDate]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        changeDateHandler(
            new Date(startDate.getTime() - (startDate.getTimezoneOffset() * 60000))?.toISOString()?.substring(0, 10),
            new Date(endDate.getTime() - (endDate.getTimezoneOffset() * 60000))?.toISOString()?.substring(0, 10)
        );
    }, [changeDateHandler, endDate, isOpen, startDate]);

    if (!isOpen) {
        return null;
    } else {
        const todayDate = getTodayDate();

        return (
            <div className={styles.mainWrapper}>
                <div className={styles.mainContainer} id="dateRangeSelector">
                    <div className={styles.mainCalendarBox}>
                        <div className={styles.dragHandle} />
                        <button
                            type='button'
                            className={styles.closeBtn}
                            onClick={closeModal}
                            aria-label="Close date range selector"
                        >
                            <X size={16} />
                        </button>
                        <div className={styles.modalHeader}>
                            <div className={styles.modalHeaderLeft}>
                                <span className={styles.statusPill}>Date Filters</span>
                                <h3 className={styles.modalTitle}>Select range for reports</h3>
                                <p className={styles.modalSubtitle}>Choose a preset or pick exact dates from both calendars.</p>
                            </div>
                            <div className={styles.rangeSummary}>
                                <CalendarDays size={14} />
                                <span>{formatDateLabel(startDate)} - {formatDateLabel(endDate)}</span>
                            </div>
                        </div>
                        <div className={styles.calendarContainer}>
                            <div className={styles.ccLeft}>
                                <div className={styles.presetTitleRow}>
                                    <Sparkles size={13} />
                                    <span>Quick ranges</span>
                                </div>
                                <div className={styles.presetList}>
                                    {PRESET_RANGES.map((range) => (
                                        <button
                                            key={range}
                                            className={`${styles.eachBtnWrapper} ${activePreset === range ? styles.activePreset : ''}`}
                                            onClick={() => setDateRange(range)}
                                            type='button'
                                        >
                                            {range}
                                        </button>
                                    ))}
                                </div>
                                <div className={styles.presetListMobile}>
                                    {PRESET_RANGES.map((range) => (
                                        <button
                                            key={`chip-${range}`}
                                            className={`${styles.eachBtnWrapperChip} ${activePreset === range ? styles.activePresetChip : ''}`}
                                            onClick={() => setDateRange(range)}
                                            type='button'
                                        >
                                            {range}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className={styles.ccRight}>
                                <div className={styles.dateHeader}>
                                    <div className={styles.eachDateWrapper}>
                                        <div className={styles.eachCalTitle}>Start Date</div>
                                        <DatePicker
                                            selected={startDate}
                                            onChange={handleStartChange}
                                            selectsStart
                                            startDate={startDate}
                                            endDate={endDate}
                                            maxDate={todayDate}
                                            highlightDates={highlightDates}
                                            inline={true}
                                            calendarClassName={styles.eachCalendar}
                                        />
                                    </div>
                                    <div className={styles.eachDateWrapper}>
                                        <div className={styles.eachCalTitle}>End Date</div>
                                        <DatePicker
                                            selected={endDate}
                                            onChange={handleEndChange}
                                            selectsEnd
                                            startDate={startDate}
                                            endDate={endDate}
                                            minDate={startDate}
                                            maxDate={todayDate}
                                            highlightDates={highlightDates}
                                            inline={true}
                                            calendarClassName={styles.eachCalendar}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
};

export default DateRangeSelector;
